import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import {
  REWARD_STATUS,
  MarketplaceItemStatus,
  MarketplaceItemV2,
  MarketplaceV2Query,
  ProcessProviderId,
  SortFn,
} from '@fgb/core';
import { FilterArgs, filterMultiple, sortBy } from '@fgb/portal-component-library/src/lib/shared/utilities';
import { SortBarOption, SortOrder } from '@fgb/portal-component-library/src/lib/shared/sort-bar';
import { CommonModule } from '@angular/common';
import { SortBarModule } from '@fgb/portal-component-library/src/lib/shared/sort-bar';

import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { MarketplaceListItemModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list-item';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { FilterStatusModalModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list/filter-status-modal';
import { MarketplaceListSortOption } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list';
import { MarketplaceListItemV2Component } from '../marketplace-list-item-v2/marketplace-list-item-v2.component';

@Component({
  selector: 'fgbcl-marketplace-list-v2',
  standalone: true,
  imports: [
    CommonModule,
    SortBarModule,
    LoadingModule,
    MarketplaceListItemModule,
    DirectivesModule,
    FGBSharedModule,
    FilterStatusModalModule,
    MarketplaceListItemV2Component,
    InlineSVGModule,
  ],
  templateUrl: './marketplace-list-v2.component.html',
  styleUrl: './marketplace-list-v2.component.scss',
})
export class MarketplaceListV2Component implements OnInit {
  readonly marketplaceItemView = input<boolean>(false);
  readonly eventItemView = input<boolean>(false);
  readonly productsItemView = input<boolean>(false);
  readonly featuredIcon = input<string>('star');
  readonly marketplaceItemTypes = input<ProcessProviderId[]>();
  readonly showStatusFilter = input<boolean>(false);
  readonly isList = input<boolean>(true);
  readonly defaultSort = input<MarketplaceListSortOption>(MarketplaceListSortOption.Points);

  private readonly marketplaceQuery = inject(MarketplaceV2Query);

  readonly isLoaded = this.marketplaceQuery.isLoaded;
  readonly sortOrderType = SortOrder;
  readonly sortBarOptions: SortBarOption[] = [
    {
      propertyToSort: 'PointValue',
      shouldToggle: true,
      order: SortOrder.Descending,
      labelASC: 'fgbcl.points.low.to.high',
      labelDESC: 'fgbcl.points.high.to.low',
    },
    {
      propertyToSort: 'ItemEndDate',
      order: SortOrder.Ascending,
      shouldToggle: false,
      labelASC: 'fgbcl.sortby.ending.soon',
    },
    {
      propertyToSort: 'StockCount',
      order: SortOrder.Descending,
      shouldToggle: true,
      labelASC: 'fgbcl.limited.availability',
      labelDESC: 'fgbcl.high.availability',
    },
    {
      propertyToSort: 'DisplayName',
      order: SortOrder.Descending,
      shouldToggle: true,
      labelASC: 'fgbcl.sortby.name.atoz',
      labelDESC: 'fgbcl.sortby.name.ztoa',
    },
  ];

  readonly filterTypesList = signal([{ label: 'reward.filter.status.all', value: MarketplaceItemStatus.All }]);
  readonly selectedState = signal<number[]>([MarketplaceItemStatus.All]);
  private readonly sort = signal<SortFn[]>(this._getSort());

  private readonly filters = computed<FilterArgs[]>(() =>
    this.selectedState().flatMap((state, index) => {
      const filter = this._getFilter(state);
      return index === 0 ? [filter] : [{ operator: 'or' as const }, filter];
    })
  );

  readonly filteredMarketplaceItems = computed(() => {
    const types = this.marketplaceItemTypes();
    return types
      ? this.marketplaceQuery
          .getMarketplaceItemsOfType(types, { sortBy: this.sort() })()
          .filter(filterMultiple(...this.filters()))
      : this.marketplaceQuery.getMarketplaceList({ sortBy: this.sort(), filterBy: this.filters() })();
  });

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit(): void {
    this.setDefaultSort();
  }

  /* ================================
   * Public Methods
   * ================================ */

  handleFilterTypesListEmitter(filterTypes: any[]) {
    this.filterTypesList.set([...filterTypes]);
  }

  handleSelectedStateEmitter(selectedState: number[]) {
    this.selectedState.set([...selectedState]);
  }

  setDefaultSort(): void {
    this.sort.set(this._getSort());
  }

  /* ================================
   * Private Helpers
   * ================================ */

  private _getFilter(state: MarketplaceItemStatus) {
    switch (state) {
      case MarketplaceItemStatus.Available:
        return (item: MarketplaceItemV2) =>
          item.Status === REWARD_STATUS.None ||
          item.Status === REWARD_STATUS.Favourite ||
          item.Status === REWARD_STATUS.LimitedStock ||
          item.Status === REWARD_STATUS.NewItem;
      case MarketplaceItemStatus.SoldOut:
        return (item: MarketplaceItemV2) => item.Status === REWARD_STATUS.SoldOut;
      case MarketplaceItemStatus.ComingSoon:
        return (item: MarketplaceItemV2) => item.Status === REWARD_STATUS.ComingSoon;
      case MarketplaceItemStatus.NotEnoughPoints:
        return (item: MarketplaceItemV2) => item.Status === REWARD_STATUS.NotEnoughPoints;
      case MarketplaceItemStatus.MemberLimitReached:
        return (item: MarketplaceItemV2) => item.Status === REWARD_STATUS.LimitReached;
      default:
        return () => true;
    }
  }

  private _getSort(): SortFn[] {
    switch (this.defaultSort()) {
      case MarketplaceListSortOption.Points:
        return [sortBy('PointValue')];
      case MarketplaceListSortOption.Ordinal:
        return [sortBy('Ordinal')];
      case MarketplaceListSortOption.Status:
        const defaultOrder = [
          REWARD_STATUS.Favourite,
          REWARD_STATUS.NewItem,
          REWARD_STATUS.LimitedStock,
          REWARD_STATUS.None,
          REWARD_STATUS.DisabledManualEntry,
          REWARD_STATUS.LimitedItem,
          REWARD_STATUS.ComingSoon,
          REWARD_STATUS.ViewableOnly,
          REWARD_STATUS.SoldOut,
          REWARD_STATUS.NotEnoughPoints,
        ];

        return defaultOrder.reduce((acc, status) => {
          acc.push(
            sortBy((a: MarketplaceItemV2, b: MarketplaceItemV2) => {
              const aStatus = a.Status;
              const bStatus = b.Status;
              if (aStatus === status && bStatus === status) {
                if (status === REWARD_STATUS.LimitedStock) {
                  return b.StockCount - a.StockCount;
                }
                return 0;
              }
              if (aStatus === status) return -1;
              if (bStatus === status) return 1;
              return 0;
            }, 'asc')
          );
          return acc;
        }, [] as SortFn[]);
    }
  }
}
